import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Text } from 'foundations-components/components/Text';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import { useState } from 'react';
import { HUBSPOT_CREDITS } from 'self-service-api/constants/UnitNames';
import { useDisplayedCurrencyCode } from '../../hooks/pricing/useCurrencyDetails';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import { CustomError } from '../error-boundary/CustomErrors';
import { APP } from '../../constants/tracking/TrackingSources';
import { useHubSpotCreditsInventoryInfo } from '../../hooks/inventory/useHubSpotCreditsInventoryInfo';
import { trackAdditionalUnitsPicker } from '../../utils/tracking/trackingEventUtils';
import { calculateMaxTouchlessPurchasableQuantity } from '../../utils/quantityPackUtils';
import { useProductDetails } from '../../hooks/product/useProductDetails';
import { useProductInventory } from '../../hooks/inventory/useProductInventory';
import { PurchaseMotionContainer } from '../../containers/PurchaseMotionContainer';
import { useGrowthPopForPurchaseMotions } from '../../hooks/pricing/useGrowthPurchaseOptions';
import { useEvaluateInAppQuery } from '../../../../__generated__/chirp/com/hubspot/purchase/motions/rpc/PurchaseMotionsEvaluationService.hooks';
import { useGetOwnedQuantitiesQuery } from '../../../../__generated__/chirp/com/hubspot/buying/rpc/ProductRpc.hooks';
import { usePerMonthPriceForTerm } from '../../hooks/pricing/useGrowthPurchaseOptions';
import { HUBSPOT_CREDITS_VALUE_PROPOSITIONS } from '../../constants/tracking/TrackingSources';
import { SCREENS } from '../../constants/tracking/Screens';
import { withErrorBoundary } from '../error-boundary/withErrorBoundary';
import { SingleSkeletonLoader } from '../SingleSkeletonLoader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PricePerCredits = ({
  loading,
  monthlyCalculatedPrice,
  packagedQuantities
}) => {
  const currencyCode = useDisplayedCurrencyCode();
  if (loading) {
    return /*#__PURE__*/_jsx(SingleSkeletonLoader, {
      height: 20,
      width: 75
    });
  }
  return /*#__PURE__*/_jsx(Text, {
    typography: "heading-50",
    as: "span",
    "data-test-id": "hubspot-credits-price-per-credits",
    children: /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "priceDisplay.highlightedPrice.pricePerCredits",
      options: {
        price: /*#__PURE__*/_jsx(PriceDisplay, {
          price: monthlyCalculatedPrice,
          currencyCode: currencyCode
        }),
        packagedQuantities
      }
    })
  });
};
const BuyAdditionalCreditsCardBase = () => {
  var _ownedQuantitiesData$, _ownedQuantitiesData$2, _productInventory$pro, _productInventory$pro2, _purchaseMotionsData$, _purchaseMotionsData$2;
  const purchaseMotionLocation = 'CALCULATOR';
  const trackingSource = HUBSPOT_CREDITS_VALUE_PROPOSITIONS;
  const screen = SCREENS.HUBSPOT_CREDITS;
  const productInventory = useProductInventory({});
  const {
    ownedHubSpotCreditsTotal: ownedQuantity,
    ownedHubSpotCreditsSkuId,
    ownedHubspotCreditsApiName: apiName,
    loading: hubSpotCreditsLoading
  } = useHubSpotCreditsInventoryInfo();
  const {
    productDetails,
    loading: productDetailsLoading,
    error: productDetailsError
  } = useProductDetails({
    productApiName: apiName
  });
  const {
    data: purchaseMotionsData,
    error: purchaseMotionsError,
    loading: purchaseMotionsLoading
  } = useEvaluateInAppQuery({
    variables: {
      productApiNames: [apiName !== null && apiName !== void 0 ? apiName : ''],
      locations: [purchaseMotionLocation]
    },
    skip: !apiName
  });
  const {
    data: ownedQuantitiesData,
    loading: ownedQuantitiesLoading,
    error: ownedQuantitiesError
  } = useGetOwnedQuantitiesQuery({
    variables: {
      quantityNames: [HUBSPOT_CREDITS]
    }
  });
  const currencyCode = useDisplayedCurrencyCode();
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const {
    commitmentTerm,
    paymentTerm
  } = useGrowthPopForPurchaseMotions({
    productApiName: apiName,
    term: queryParamTerm
  });
  const {
    price: monthlyCalculatedPrice,
    loading: monthlyCalculatedPriceLoading,
    error: monthlyCalculatedPriceError
  } = usePerMonthPriceForTerm({
    productApiName: apiName !== null && apiName !== void 0 ? apiName : '',
    term: queryParamTerm
  });
  const [selectedQuantity, setSelectedQuantity] = useState(0);
  const includedWithPlan = (_ownedQuantitiesData$ = ownedQuantitiesData === null || ownedQuantitiesData === void 0 || (_ownedQuantitiesData$2 = ownedQuantitiesData.quantities[HUBSPOT_CREDITS]) === null || _ownedQuantitiesData$2 === void 0 ? void 0 : _ownedQuantitiesData$2.includedQuantity) !== null && _ownedQuantitiesData$ !== void 0 ? _ownedQuantitiesData$ : 0;
  const maxTouchlessPurchasableQuantity = productDetails === null || productDetails === void 0 ? void 0 : productDetails.maxTouchlessPurchasableQuantity;
  const packagedQuantities = productDetails === null || productDetails === void 0 ? void 0 : productDetails.packagedQuantitiesMap.HUBSPOT_CREDITS;
  const calculatedMaxQuantity = calculateMaxTouchlessPurchasableQuantity(maxTouchlessPurchasableQuantity);
  const minQuantityForStepper = 0;
  const ownedProducts = (_productInventory$pro = (_productInventory$pro2 = productInventory.products) === null || _productInventory$pro2 === void 0 ? void 0 : _productInventory$pro2.map(product => product.productApiName)) !== null && _productInventory$pro !== void 0 ? _productInventory$pro : [];
  const trackingData = {
    app: APP,
    screen,
    uniqueId: `${trackingSource}-${apiName}`
  };
  const purchaseMotion = (_purchaseMotionsData$ = purchaseMotionsData === null || purchaseMotionsData === void 0 || (_purchaseMotionsData$2 = purchaseMotionsData.purchaseMotionsMapping.productsByLocationString[purchaseMotionLocation]) === null || _purchaseMotionsData$2 === void 0 || (_purchaseMotionsData$2 = _purchaseMotionsData$2.purchaseMotionsByProductApiName[apiName !== null && apiName !== void 0 ? apiName : '']) === null || _purchaseMotionsData$2 === void 0 || (_purchaseMotionsData$2 = _purchaseMotionsData$2.purchaseMotions) === null || _purchaseMotionsData$2 === void 0 ? void 0 : _purchaseMotionsData$2[0]) !== null && _purchaseMotionsData$ !== void 0 ? _purchaseMotionsData$ : 'TALK_TO_SALES_FALLBACK';
  const isTouchlessPurchase = purchaseMotion === 'BUY_TOUCHLESS';

  // Disable button only for touchless purchases when user hasn't selected additional quantity
  const disabledButton = isTouchlessPurchase && selectedQuantity <= minQuantityForStepper;
  const loading = monthlyCalculatedPriceLoading || hubSpotCreditsLoading || ownedQuantitiesLoading || purchaseMotionsLoading || productDetailsLoading;
  const handleStepperChange = evt => {
    if (!packagedQuantities) return;
    const input = Math.round(parseInt(evt.target.value, 10) / packagedQuantities);
    if (isNaN(input)) return;
    const newQuantity = Math.max(minQuantityForStepper, Math.min(calculatedMaxQuantity, input));
    trackAdditionalUnitsPicker({
      action: `stepper ${newQuantity > selectedQuantity ? 'increase' : 'decrease'}`,
      trackingSource,
      activeCurrencyCode: currencyCode,
      skuId: ownedHubSpotCreditsSkuId !== null && ownedHubSpotCreditsSkuId !== void 0 ? ownedHubSpotCreditsSkuId : 0,
      ownedUnitQuantity: ownedQuantity || 0,
      displayedUnitQuantity: selectedQuantity * packagedQuantities,
      productName: apiName,
      ownedProducts
    });
    setSelectedQuantity(newQuantity);
  };
  if (monthlyCalculatedPriceError) {
    throw new CustomError('Failed calculating price', {
      error: monthlyCalculatedPriceError
    });
  }
  if (productDetailsError) {
    throw new CustomError('Failed getting product details', {
      error: productDetailsError
    });
  }
  if (ownedQuantitiesError) {
    throw new CustomError('Failed getting owned quantities', {
      error: ownedQuantitiesError
    });
  }
  if (purchaseMotionsError) {
    throw new CustomError('Failed getting purchase motions', {
      error: purchaseMotionsError
    });
  }
  if (!apiName || !monthlyCalculatedPrice || !packagedQuantities || !ownedHubSpotCreditsSkuId) {
    return null;
  }
  if (loading) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
  return /*#__PURE__*/_jsxs(CardWrapper, {
    inlineSize: "100%",
    paddingBlock: "800",
    paddingInline: "1000",
    marginBlockEnd: "0",
    display: "flex",
    flexDirection: "column",
    gap: "600",
    "data-test-id": `${apiName}-merch-card`,
    "data-selenium-id": `${apiName}-merch-card`,
    children: [/*#__PURE__*/_jsxs(CardSection, {
      padding: "0",
      gap: "300",
      children: [/*#__PURE__*/_jsx(Text, {
        whiteSpace: "nowrap",
        as: "h2",
        typography: "heading-200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.pages.hubspot-credits.info.card.buyAdditionalCredits"
        })
      }), /*#__PURE__*/_jsx(Text, {
        as: "p",
        typography: "body-100",
        "data-test-id": "hubspot-credits-included-and-additional-credits",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "pricingPage.pages.hubspot-credits.info.card.includedAndAdditionalCredits",
          options: {
            includedQty: /*#__PURE__*/_jsx(FormattedNumber, {
              value: includedWithPlan
            }),
            additionalQty: /*#__PURE__*/_jsx(FormattedNumber, {
              value: ownedQuantity * packagedQuantities
            })
          }
        })
      })]
    }), /*#__PURE__*/_jsxs(CardSection, {
      padding: "0",
      gap: "300",
      display: "flex",
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx("div", {
        "data-test-id": `${trackingSource}-${apiName}-stepper-input-container`,
        children: /*#__PURE__*/_jsx(StepperInput, {
          "data-test-id": `${trackingSource}-${apiName}-stepper-input`,
          "data-selenium-id": `${trackingSource}-${apiName}-stepper-input`,
          maxInlineSize: "140px",
          stepSize: packagedQuantities,
          value: selectedQuantity * packagedQuantities,
          min: minQuantityForStepper * packagedQuantities,
          max: calculatedMaxQuantity * packagedQuantities,
          onChange: handleStepperChange
        })
      }), /*#__PURE__*/_jsx(PricePerCredits, {
        loading: loading,
        monthlyCalculatedPrice: monthlyCalculatedPrice,
        packagedQuantities: packagedQuantities
      })]
    }), /*#__PURE__*/_jsx(CardSection, {
      padding: "0",
      children: /*#__PURE__*/_jsx(PurchaseMotionContainer, {
        productApiName: apiName,
        location: purchaseMotionLocation,
        trackingData: trackingData,
        skipInterstitial: true,
        purchaseRequest: {
          products: [{
            productApiName: apiName,
            totalQuantity: ownedQuantity + selectedQuantity
          }],
          commitmentTerm: commitmentTerm !== null && commitmentTerm !== void 0 ? commitmentTerm : undefined,
          paymentTerm: paymentTerm !== null && paymentTerm !== void 0 ? paymentTerm : undefined,
          currencyCode
        },
        disabled: disabledButton
      })
    })]
  });
};
export const BuyAdditionalCreditsCard = withErrorBoundary(BuyAdditionalCreditsCardBase, {
  fallback: null,
  errorBoundaryName: 'BuyAdditionalCreditsCard'
});